import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../../features/auth/services/auth.service';
import { API_ROUTES } from '../constants/injection-tokens';
import { LocalStorageService } from '../services/local-storage.service';
import { isRefreshTokenRoute } from './auth-exception-routes.const';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const tokenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const localStorageService = inject(LocalStorageService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const apiRoutes = inject(API_ROUTES);

  const addTokenHeader = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
    req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

  const redirectToRegister = (error: unknown): Observable<never> => {
    isRefreshing = false;
    localStorageService.clear();
    router.navigate(['/register']);
    return throwError(() => error);
  };

  const refreshAndRetry = (
    req: HttpRequest<unknown>,
    nextFn: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> => {
    if (isRefreshing) {
      return refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((token) => nextFn(addTokenHeader(req, token))),
      );
    }

    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response) => {
        isRefreshing = false;
        if (!response.AccessToken) {
          return throwError(() => new Error('No access token in refresh response'));
        }

        refreshTokenSubject.next(response.AccessToken);
        return nextFn(addTokenHeader(req, response.AccessToken));
      }),
      catchError((error) => redirectToRegister(error)),
    );
  };

  const refreshToken = localStorageService.getRefreshToken();
  const refreshTokenRoute = isRefreshTokenRoute(request.url, apiRoutes);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && refreshToken && !refreshTokenRoute) {
        return refreshAndRetry(request, next);
      }

      return throwError(() => error);
    }),
  );
};
